import React, { useCallback, useEffect, useRef, useState } from "react";

import Icon from "components/Icons/Icon";

/**
 * Affordance for a horizontally scrolling row.
 *
 * Reported from an iPhone 16 (86cb933wt): the trending country row and the "Popular right now"
 * cards scroll sideways, and nothing says so. The cards bleed off the right edge, which reads
 * as a layout that got cut off rather than as an invitation to swipe.
 *
 * Ported from `ScrollRow.tsx` in the Figma Make build, whose own note is that arrows also give
 * mouse-only visitors a way through a row that otherwise needs a trackpad swipe. Kept small
 * on phones and full size from `md` up, as it has them.
 *
 * A disabled arrow stays visible at low opacity rather than unmounting — a control that
 * appears and disappears as you scroll is its own kind of confusing, and a row with both
 * arrows dimmed still tells you it is a row.
 */
export function useScrollRow<T extends HTMLElement = HTMLDivElement>() {
	const ref = useRef<T>(null);
	const [canLeft, setCanLeft] = useState(false);
	const [canRight, setCanRight] = useState(false);

	const update = useCallback(() => {
		const el = ref.current;
		if (!el) return;
		setCanLeft(el.scrollLeft > 4);
		setCanRight(el.scrollLeft < el.scrollWidth - el.clientWidth - 4);
	}, []);

	useEffect(() => {
		const el = ref.current;
		if (!el) return undefined;
		update();
		el.addEventListener("scroll", update, { passive: true });
		window.addEventListener("resize", update);
		// Re-measure once images and fonts settle — the row is narrower than its content only
		// after the photos have laid out, so a single measure on mount reads "not scrollable".
		const timer = setTimeout(update, 300);
		return () => {
			el.removeEventListener("scroll", update);
			window.removeEventListener("resize", update);
			clearTimeout(timer);
		};
	}, [update]);

	const scrollByDir = useCallback((dir: 1 | -1) => {
		const el = ref.current;
		if (!el) return;
		el.scrollBy({ left: dir * Math.max(240, el.clientWidth * 0.8), behavior: "smooth" });
	}, []);

	return { ref, canLeft, canRight, scrollByDir, arrowProps: { canLeft, canRight, scrollByDir } };
}

export interface ScrollArrowsProps {
	canLeft: boolean;
	canRight: boolean;
	scrollByDir: (dir: 1 | -1) => void;
	ariaLabel?: string;
}

export function ScrollArrows({ canLeft, canRight, scrollByDir, ariaLabel = "row" }: ScrollArrowsProps) {
	return (
		<div className="scroll-arrows">
			{([
				[-1, canLeft, `${ariaLabel}: scroll left`, "chevron-left"],
				[1, canRight, `${ariaLabel}: scroll right`, "chevron-right"],
			] as const).map(([dir, enabled, label, icon]) => (
				<button
					key={label}
					type="button"
					aria-label={label}
					disabled={!enabled}
					onClick={() => scrollByDir(dir)}
					className={`scroll-arrows__btn ${enabled ? "" : "scroll-arrows__btn--off"}`}
				>
					<Icon name={icon} size={9} fill="#0f3e4a" />
				</button>
			))}
		</div>
	);
}
